// src/pages/ConfirmMetadataPage.js
/**
 * ConfirmMetadataPage (ReviewMetadataPage)
 * ---------------------------------------
 * If the user's demographics were updated more than RECERT_MONTHS ago, show a
 * confirmation prompt ("Have any details changed?").
 *
 * Gate logic:
 *  1) Fetch newest metadata timestamp (no JSON download) via ensureProfileInfo().
 *  2) If no metadata exists -> first-time user -> go to /form
 *  3) If newest <= RECERT_MONTHS -> skip confirm -> go to requested recording
 *  4) Otherwise fetch JSON (ensureProfile with force) and render confirm UI.
 */

import React, { useEffect, useState, memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ReviewStep from "../components/form/ReviewStep";
import Spinner from "../components/Spinner";

import { questions } from "../utils/form/formQuestions";
import { filterQuestions } from "../utils/form/formRuntime";
import { useCurrentUserClinicMapping } from "../context/UserClinicMappingContext";
import { useProfilePrefetch } from "../context/ProfilePrefetchContext";
import { useAuth } from "../context/AuthContext";
import { RECERT_MS, TTL_MS } from "../utils/constants";
import { trackEvent } from "../utils/analytics/track";
import { getSafeInternalNextPath } from "../utils/navigation/recordingModeNav";
import { mergeProfileWithClinicMapping } from "../utils/recordingMode/mergeProfileWithClinicMapping";

function ReviewMetadataPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const mapping = useCurrentUserClinicMapping();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = getSafeInternalNextPath(searchParams.get("next"));

  const [formData, setFormData] = useState(null); // null = loading
  const [filteredQs, setFiltered] = useState([]);

  const {
    getCachedProfile,
    ensureProfile,
    getCachedProfileInfo,
    ensureProfileInfo,
  } = useProfilePrefetch();

  useEffect(() => {
    if (!user?.uid) return;
    if (!mapping.resolved) return;

    let alive = true;

    (async () => {
      try {
        trackEvent("confirm_metadata_gate_start");

        // 1) Fast path: newest timestamp only (no JSON download)
        const cachedInfo = getCachedProfileInfo(user.uid);
        let info = cachedInfo?.status === "ready" ? cachedInfo : null;

        if (!info) {
          info = await ensureProfileInfo({ uid: user.uid, ttlMs: TTL_MS });
        }

        if (!alive) return;

        // No metadata -> first-time user
        if (!info?.latestMs) {
          trackEvent("confirm_metadata_gate_outcome", {
            outcome: "no_metadata",
          });
          navigate(`/form?next=${encodeURIComponent(nextPath)}`, {
            replace: true,
            state: { returnTo: nextPath },
          });
          return;
        }

        // Up-to-date -> skip confirmation
        const ageMs = Date.now() - info.latestMs;
        if (ageMs <= RECERT_MS) {
          trackEvent("confirm_metadata_gate_outcome", {
            outcome: "up_to_date",
          });
          navigate(nextPath, { replace: true });
          return;
        }

        // 2) Only now: fetch JSON and render confirm screen
        const cached = getCachedProfile(user.uid);
        let data = cached?.status === "ready" ? cached.data : null;

        if (!data) {
          data = await ensureProfile({
            uid: user.uid,
            ttlMs: TTL_MS,
            force: true, // ensure we pull newest when showing the confirm UI
          });
        }

        if (!alive) return;

        if (!data) {
          trackEvent("confirm_metadata_gate_outcome", {
            outcome: "missing_profile_json",
          });
          navigate(`/form?next=${encodeURIComponent(nextPath)}`, {
            replace: true,
            state: { returnTo: nextPath },
          });
          return;
        }

        const merged = mergeProfileWithClinicMapping(data, mapping);

        setFormData(merged);
        setFiltered(filterQuestions(questions, merged, user));
        trackEvent("confirm_metadata_view");
      } catch (err) {
        console.error("Could not evaluate demographic recert gate:", err);
        trackEvent("confirm_metadata_gate_outcome", { outcome: "error" });
        navigate(`/form?next=${encodeURIComponent(nextPath)}`, {
          replace: true,
          state: { returnTo: nextPath },
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, [
    user?.uid,
    user,
    mapping,
    navigate,
    getCachedProfile,
    ensureProfile,
    getCachedProfileInfo,
    ensureProfileInfo,
    nextPath,
  ]);

  if (!mapping.resolved || formData === null) return <Spinner />;

  return (
    <div className="w-full max-w-lg h-full p-4 flex flex-col justify-start items-center gap-6">
      <ReviewStep
        title={t("confirmMetadata.title")}
        questions={filteredQs}
        formData={formData}
        translationForm="form"
      />

      <div className="flex w-full justify-between pt-4">
        <button
          type="button"
          className="outlined-button"
          onClick={() => {
            trackEvent("confirm_metadata_click_yes");
            navigate(`/form?next=${encodeURIComponent(nextPath)}`, {
              state: { prefill: formData, returnTo: nextPath },
            });
          }}
        >
          {t("confirmMetadata.yes")}
        </button>

        <button
          type="button"
          className="primary-button"
          onClick={() => {
            trackEvent("confirm_metadata_click_no");
            navigate(nextPath);
          }}
        >
          {t("confirmMetadata.no")}
        </button>
      </div>
    </div>
  );
}

export default memo(ReviewMetadataPage);
